const HEAVY_VEHICLE_CAPABILITY_KEYS = [
  "trailer_parking",
  "truck_only",
  "height_restriction",
  "drive_through",
  "dangerous_goods",
  "rigid_vehicles",
  "overnight_parking",
  "barrier",
];

const HEAVY_VEHICLE_DIMENSION_KEYS = [
  "height",
  "width",
  "length",
  "mass",
  "cable_length",
  "turning_radius",
];

const hasNonZeroNumber = (value) => {
  if (value == null || value === "") {
    return false;
  }

  const num = Number(value);
  return !Number.isNaN(num) && num !== 0;
};

export function buildParkingSpacesSummary(spaces = [], t) {
  const count = spaces.length;

  if (count === 0) {
    return t("site.location.summaryNoneParking");
  }

  const refs = spaces
    .map((space) => String(space?.physicalReference ?? "").trim())
    .filter(Boolean);

  const countLabel = t("site.location.summaryParking", { count });

  if (refs.length === 0) {
    return countLabel;
  }

  if (refs.length <= 3) {
    return `${countLabel} · ${refs.join(", ")}`;
  }

  return `${countLabel} · ${refs.slice(0, 3).join(", ")} ${t("site.location.summaryMore", { count: refs.length - 3 })}`;
}

export function buildRelatedLocationsSummary(locations = [], t) {
  const count = locations.length;

  if (count === 0) {
    return t("site.location.summaryNoneRelated");
  }

  return t("site.location.summaryRelated", { count });
}

export function buildOpeningHoursSummary(openingTimes, t) {
  if (!openingTimes) {
    return t("site.location.summaryNoHours");
  }

  const regularCount = openingTimes.regular_hours?.length ?? 0;
  const openingExceptions = openingTimes.exceptional_openings?.length ?? 0;
  const closingExceptions = openingTimes.exceptional_closings?.length ?? 0;
  const parts = [];

  if (regularCount > 0) {
    parts.push(t("site.location.summaryRegular", { count: regularCount }));
  }

  if (openingExceptions > 0) {
    parts.push(t("site.location.summaryOpening", { count: openingExceptions }));
  }

  if (closingExceptions > 0) {
    parts.push(t("site.location.summaryClosing", { count: closingExceptions }));
  }

  if (parts.length === 0) {
    return t("site.location.summaryNoHours");
  }

  return parts.join(" · ");
}

export function buildHeavyVehicleSummary(vehicle = {}, suitabilityOptions = [], t) {
  const parts = [];

  if (vehicle.suitability) {
    const match = suitabilityOptions.find(
      (option) => option.value === vehicle.suitability
    );
    parts.push(match?.label ?? vehicle.suitability);
  }

  const amenityCount = vehicle.amenities?.length ?? 0;
  if (amenityCount > 0) {
    parts.push(t("site.location.summaryAmenity", { count: amenityCount }));
  }

  const cableSideCount = vehicle.cable_side?.length ?? 0;
  if (cableSideCount > 0) {
    parts.push(t("site.location.summaryCableSide", { count: cableSideCount }));
  }

  const enabledCapabilities = HEAVY_VEHICLE_CAPABILITY_KEYS.filter(
    (key) => vehicle[key]
  ).length;
  if (enabledCapabilities > 0) {
    parts.push(t("site.location.summaryCapability", { count: enabledCapabilities }));
  }

  const hasCustomDimensions = HEAVY_VEHICLE_DIMENSION_KEYS.some((key) =>
    hasNonZeroNumber(vehicle[key])
  );
  if (hasCustomDimensions) {
    parts.push(t("site.location.summaryCustomDimensions"));
  }

  if (parts.length === 0) {
    return t("site.location.summaryNoHeavy");
  }

  return parts.join(" · ");
}
